import { Button, FooterBanner, TextBlock } from "@bcc-ui/ui-components";
import React from "react";
import { useTranslation } from "react-i18next";

import ManageAssetCustomViewForm, {
    AssetCustomViewFormValues,
} from "components/asset-management-system/custom-views/ManageAssetCustomViewForm";
import { SubpageLayout } from "components/layout/subpage-layout/SubpageLayout";
import { enableCustomAssetViewRoutes } from "components/router/Routes";
import { SuccessModal } from "components/success-modal/SuccessModal";
import {
    ASSET_OVERVIEW,
    AssetCustomView,
    AssetCustomViewFilterEntry,
    toFilterEntryArray,
    withDateRangeFilters,
} from "domain/asset-management/assets";
import { assetViewService } from "services/asset-management/AssetCustomViewService";
import { logger } from "utils/logging";

const FORM_ID = "createAssetViewForm";
const ASSET_VIEW_PATH = "/assets/views/";

const getAllViewNames = (views: AssetCustomView[] = []): string[] =>
    views.flatMap((view) => [view.name, ...getAllViewNames(view.childrenViews ?? [])]);

export const findViewByName = (views: AssetCustomView[], name: string): AssetCustomView | undefined => {
    for (const view of views) {
        if (view.name === name) {
            return view;
        }
        if (view.childrenViews) {
            const found = findViewByName(view.childrenViews, name);
            if (found !== undefined) {
                return found;
            }
        }
    }
    return undefined;
};

interface SuccessModalState {
    isOpen: boolean;
    title: string;
    message: string;
    viewUuid: string;
    viewName: string;
    views: AssetCustomView[];
}

interface Props {
    viewDetails?: AssetCustomView;
    isDuplicate?: boolean;
    parentUuid?: string;
    initialFilters?: AssetCustomViewFilterEntry[];
    dateRange?: [string, string];
    selectedAssetIds?: string[];
    folders?: AssetCustomView[];
    openFileToFilterTab?: boolean;
}

export const CreateAssetCustomViewSubpage = (props: Props) => {
    const { t } = useTranslation();
    const isEdit = props.viewDetails !== undefined && !props.isDuplicate;
    const abortControllerRef = React.useRef<AbortController | undefined>(undefined);
    const viewDetailFilters = toFilterEntryArray(props.viewDetails?.filters);

    const [isLoading, setIsLoading] = React.useState(false);
    const [existingNames, setExistingNames] = React.useState<string[]>([]);
    const [isSubmitDisabled, setIsSubmitDisabled] = React.useState(false);
    const [successModalState, setSuccessModalState] = React.useState<SuccessModalState>({
        isOpen: false,
        title: "",
        message: "",
        viewUuid: "",
        viewName: "",
        views: [],
    });

    React.useEffect(() => {
        fetchExistingViews();
    }, []);

    const fetchExistingViews = () => {
        assetViewService
            .fetchAssetRelatedViews()
            .then((response: AssetCustomView[]) => {
                const allNames = getAllViewNames(response);
                const ownName = isEdit ? props.viewDetails?.name : undefined;
                setExistingNames(ownName ? allNames.filter((name) => name !== ownName) : allNames);
            })
            .catch((err: Error) => {
                logger.error("Failed to fetch asset views: ", err);
            });
    };

    const handleSubmit = async (values: AssetCustomViewFormValues) => {
        setIsLoading(true);
        setSuccessModalState({
            isOpen: true,
            title: isEdit
                ? t("AssetManagement.editAssetCustomView.title", { name: props.viewDetails?.name })
                : t("AssetManagement.createAssetCustomView.title"),
            message: "",
            viewUuid: "",
            viewName: props.viewDetails?.name ?? "",
            views: [],
        });
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        const filters = withDateRangeFilters(values.filters, values.dateRange?.[0], values.dateRange?.[1]);

        try {
            if (isEdit && props.viewDetails) {
                await assetViewService.editAssetCustomView(
                    props.viewDetails!.uuid,
                    {
                        uuid: props.viewDetails.uuid,
                        name: values.name,
                        folder: false,
                        columns: values.columns,
                        filters: filters,
                        parentUuid: values.parentFolder,
                        description: values.description,
                    },
                    abortController
                );
                const views = await assetViewService.fetchAssetRelatedViews();
                setSuccessModalState({
                    isOpen: true,
                    title: t("EditReportView.viewEdit.success.title"),
                    message: t("AssetManagement.editAssetCustomView.successMessage"),
                    viewUuid: props.viewDetails.uuid,
                    viewName: values.name,
                    views,
                });
            } else {
                await assetViewService.createAssetCustomView({
                    name: values.name,
                    uuid: "",
                    folder: false,
                    columns: values.columns,
                    filters: filters,
                    parentUuid: values.parentFolder,
                    description: values.description,
                });

                const views = await assetViewService.fetchAssetRelatedViews();
                const createdView = findViewByName(views, values.name);
                if (createdView) {
                    if (props.selectedAssetIds && props.selectedAssetIds.length > 0) {
                        await assetViewService.addAssetsToGroup(createdView.uuid, props.selectedAssetIds);
                    }
                    setSuccessModalState({
                        isOpen: true,
                        title: t("Common.success"),
                        message: t("AssetManagement.createAssetCustomView.successMessage", {
                            name: values.name,
                        }),
                        viewUuid: createdView.uuid,
                        viewName: values.name,
                        views,
                    });
                } else {
                    throw Error("Created asset view was not found by name in the fetched views");
                }
            }
        } catch {
            setSuccessModalState({ isOpen: false, title: "", message: "", viewUuid: "", viewName: "", views: [] });
            setIsSubmitDisabled(true);
        } finally {
            setIsLoading(false);
        }
    };

    const showForm = () => {
        return (
            <ManageAssetCustomViewForm
                formId={FORM_ID}
                onSubmit={handleSubmit}
                folder={false}
                existingNames={existingNames}
                isDuplicate={props.isDuplicate}
                onSubmitDisabledChange={setIsSubmitDisabled}
                folders={props.folders}
                isFilterTabSelected={props.openFileToFilterTab}
                initialValues={
                    props.viewDetails
                        ? {
                              name: props.viewDetails.name,
                              private: false,
                              columns: props.viewDetails.columns,
                              filters: viewDetailFilters,
                              parentFolder: props.viewDetails.parentUuid ?? undefined,
                              description: props.viewDetails.description ?? "",
                              dateRange:
                                  props.viewDetails.startDate && props.viewDetails.endDate
                                      ? [props.viewDetails.startDate, props.viewDetails.endDate]
                                      : undefined,
                          }
                        : props.initialFilters
                          ? {
                                name: "",
                                private: false,
                                columns: [...new Set(ASSET_OVERVIEW)],
                                filters: props.initialFilters,
                                parentFolder: props.parentUuid ?? undefined,
                                description: "",
                                dateRange: props.dateRange,
                            }
                          : {
                                name: "",
                                private: true,
                                columns: [...new Set(ASSET_OVERVIEW)],
                                filters: [],
                                parentFolder: props.parentUuid ?? undefined,
                                description: "",
                            }
                }
            />
        );
    };

    React.useEffect(() => () => abortControllerRef.current?.abort(), []);

    const closeModal = () => {
        enableCustomAssetViewRoutes(successModalState.views);
        window.location.replace(ASSET_VIEW_PATH + successModalState.viewUuid);
    };

    return (
        <>
            <SubpageLayout
                visible
                title={
                    isEdit
                        ? t("AssetManagement.editAssetCustomView.title", { name: props.viewDetails?.name })
                        : t("AssetManagement.createAssetGroup")
                }
                buttons={
                    <Button
                        variant="Primary"
                        type="submit"
                        form={FORM_ID}
                        disabled={isSubmitDisabled}
                        isLoading={isLoading}
                    >
                        {isEdit ? t("Common.save") : t("Common.create")}
                    </Button>
                }
                footerBanner={isSubmitDisabled && <FooterBanner text={t("Common.fixErrors")} />}
            >
                {showForm()}
            </SubpageLayout>
            <SuccessModal
                isOpen={successModalState.isOpen}
                hideModal={closeModal}
                title={successModalState.title}
                message={<TextBlock>{successModalState.message}</TextBlock>}
                isLoading={isLoading}
            />
        </>
    );
};
